import React, { useRef, useEffect } from "react";
import { InputMask } from '@react-input/mask';
import CONFIG from "../../../config";
import { 
    ID_TEST_BUTTON_PAY, ID_TEST_CHECKBOX_AGREE, ID_TEST_INPUT_CONTRACT, 
    ID_TEST_INPUT_PHONE, ID_TEST_INPUT_RAW_AMOUNT, ID_TEST_SPAN_COMISSION, ID_TEST_SPAN_TOTAL 
} from "../../../spec/config";

declare global {
    interface Window {
        onSubmit: (token: string) => void
    }
}

const { RECAPTCHA_KEY, SUBMIT_URL, LINKS, MAX_AMOUNT, MIN_AMOUNT, MAX_CONTRACT_LENGTH, MIN_CONTRACT_LENGTH, PHONE_LENGTH } = CONFIG;

const TBankPaymentForm: React.FC = () => {
    // явные элементы
    const contractRef = useRef<HTMLInputElement>(null!);
    const rawAmountRef = useRef<HTMLInputElement>(null!);
    const phoneRef = useRef<HTMLInputElement>(null!);
    const check2Ref = useRef<HTMLInputElement>(null!);
    const paymentButtonRef = useRef<HTMLButtonElement>(null!);
    // скрытые элементы, убрать их из разметки
    const tokenRef = useRef<HTMLInputElement>(null!); // token приходит в ответе от рекапчи гугла
    const tamountRef = useRef<HTMLInputElement>(null!); // tamount
    const bankPercentRef = useRef<HTMLInputElement>(null!); // bank_percent
    // пользовательские данные, которые просто показываем пользователю, их убрать тоже
    const tamountVRef = useRef<HTMLInputElement>(null!);
    const bankPercentVRef = useRef<HTMLInputElement>(null!);
    const paymentRef = useRef<HTMLFormElement>(null!);

    useEffect(() => {
        window.onSubmit = onSubmit;
    });

    function nessearyChecks(contract: string, amount: number, phone: string): boolean {
        return !!(contract !== "" && amount && (amount >= MIN_AMOUNT || amount === 1) && phone !== "" && phone.length >= PHONE_LENGTH && check2Ref.current.checked);
    }

    function onSubmit(token: string): void {
        tokenRef.current.value = token;
        const contract = contractRef.current.value;
        const amount = Number(rawAmountRef.current.value);
        const phone = phoneRef.current.value;
        if (nessearyChecks(contract, amount, phone)) {
            paymentRef.current.submit();
        }
    }

    function getFee(amount: number): [totalAmount: number, fee: number] {
        let fee;
        let totalAmount;
        if (amount < 15000) {
            if (amount < 1500) {
                totalAmount = amount / 0.981;
                fee = totalAmount - amount;
            } else {
                totalAmount = amount / 0.9958;
                fee = totalAmount - amount;
                if (fee <= 20) {
                    fee = 20;
                    totalAmount = amount + fee;
                }
            }
        } else {
            totalAmount = amount / 0.9958;
            fee = totalAmount - amount;
        }
        return [totalAmount, fee];
    }

    const testReadiness = (): void => {
        const contract = contractRef.current.value;
        const amount = Number(rawAmountRef.current.value);
        const phone = phoneRef.current.value;
        const btn = paymentButtonRef.current;
        const enabled = nessearyChecks(contract, amount, phone) && (contract.length === MIN_CONTRACT_LENGTH || contract.length === MAX_CONTRACT_LENGTH);
        btn.disabled = !enabled;
        if (!btn.disabled) {
            btn.classList.remove("bt-gray");
        } else {
            btn.classList.add("bt-gray");
        }
    }

    const updateAmount = () => {
        let amount = Number(rawAmountRef.current.value);
        const currency = new Intl.NumberFormat("ru-RU", { style: "currency", currency: "RUB", minimumFractionDigits: 2 });
        if (amount === 1500) {
            amount = 1499;
            rawAmountRef.current.value = `${amount}`;
        }
        const [totalAmount, fee] = getFee(amount);
        if (isNaN(fee)) {
            // служебные данные
            bankPercentRef.current.value = currency.format(0);
            tamountRef.current.value = currency.format(0);
            // пользовательские данные
            bankPercentVRef.current.innerHTML = currency.format(0);
            tamountVRef.current.innerHTML = currency.format(0);
        } else {
            // служебные данные
            bankPercentRef.current.value = currency.format(fee);
            tamountRef.current.value = currency.format(totalAmount);
            // пользовательские данные
            bankPercentVRef.current.innerHTML = currency.format(fee);
            tamountVRef.current.innerHTML = currency.format(totalAmount);
        }
        testReadiness();
        return true;
    }

    const updateKeyAmount = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isFinite(Number(event.key))) {
            const v = rawAmountRef.current.value;
            rawAmountRef.current.value = v.replace(/\D/g, '');
            return false;
        }
        if (Number(rawAmountRef.current.value) > MAX_AMOUNT) {
            rawAmountRef.current.value = `${MAX_AMOUNT}`;
        }
        updateAmount();
    }

    const contractKeyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (contractRef.current.value.length > MAX_CONTRACT_LENGTH) {
            contractRef.current.value = contractRef.current.value.slice(0, MAX_CONTRACT_LENGTH);
        }
        return event.charCode >= 48 && event.charCode <= 57;
    }
    return (
        <form ref={paymentRef} action={SUBMIT_URL} method="GET">

            {/* Служебные данные */}
            {/* Нужны для дополнения формы указанными параметрами */}
            <input ref={tokenRef} type="hidden" name="token" />
            <input ref={tamountRef} type="hidden" name="tamount" />
            <input ref={bankPercentRef} type="hidden" name="bank_percent" />
            {/* Убрать их */}

            <div className="pay__money__inputs">
                <input
                    id={ID_TEST_INPUT_CONTRACT}
                    ref={contractRef}
                    name="contract"
                    type="number"
                    tabIndex={1}
                    placeholder="Номер договора"
                    autoFocus
                    onKeyUp={contractKeyPressHandler}
                    required
                />
                <InputMask
                    id={ID_TEST_INPUT_PHONE}
                    ref={phoneRef}
                    name="phone"
                    type="tel"
                    spellCheck="false"
                    tabIndex={2}
                    placeholder="+7 (   ) ___-__-__"
                    onChange={testReadiness}
                    required
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    mask="+7 (___) ___-__-__" replacement={{ _: /\d/ }} />
                <input
                    id={ID_TEST_INPUT_RAW_AMOUNT}
                    ref={rawAmountRef}
                    name="raw_amount"
                    type="number"
                    placeholder={`Сумма (не менее ${MIN_AMOUNT} ₽)`}
                    tabIndex={3}
                    onKeyUp={updateKeyAmount}
                    onKeyDown={updateKeyAmount}
                    onKeyPress={updateKeyAmount}
                    onChange={updateAmount}
                    required
                />
            </div>
            <div className="pay__money__result">
                <div className="result__summ">
                    <span>Итого к оплате:</span>
                    <span
                        id={ID_TEST_SPAN_TOTAL}
                        ref={tamountVRef}
                    >0 ₽</span>
                </div>
                <div className="bank__comission">
                    <span>В том числе, комиссия:</span>
                    <span 
                        id={ID_TEST_SPAN_COMISSION}
                        ref={bankPercentVRef}
                    >0 ₽</span>
                    <p>Обратите внимание, что при оплате может взыматься дополнительная комиссия банка, выпустившего карту</p>
                </div>
                <label>
                    <input
                        id={ID_TEST_CHECKBOX_AGREE}
                        ref={check2Ref}
                        onChange={testReadiness}
                        tabIndex={4}
                        type="checkbox"
                        name="agreements"
                        required
                    />
                    <span>С
                        <a
                            href={LINKS.PAY_ROOLS_PDF}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                        > правилами </a>
                        ознакомлен
                    </span>
                </label>
                <button
                    id={ID_TEST_BUTTON_PAY}
                    ref={paymentButtonRef}
                    className="g-recaptcha bt-gray"
                    data-sitekey={RECAPTCHA_KEY}
                    tabIndex={5}
                    data-callback={'onSubmit'}
                    disabled
                >Оплатить</button>
            </div>
        </form>
    );
}

export default TBankPaymentForm;